import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  capitalize,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { FreeDayCreate } from "../../../api";
import { useQueryClient } from "react-query";

export type FreedaysBulkCreateDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (items: Array<FreeDayCreate>) => void;
};

type Row = {
  datum: string;
  countryCode: string;
  name?: string;
  error?: {
    datum?: string;
    countryCode?: string;
  };
};

const emptyRow: Row = { datum: "", countryCode: "", name: "" };

export default function FreedaysBulkCreateDialog({ open, onClose, onSubmit }: FreedaysBulkCreateDialogProps) {
  const { t: translation } = useTranslation("freedays");
  const [rows, setRows] = useState<Row[]>([{ ...emptyRow }]);

  // Always start with empty rows whenever the dialog is opened
  useEffect(() => {
    if (open) {
      setRows([{ ...emptyRow }]);
    }
  }, [open]);


  const canSubmit = useMemo(() => {
    return (
              rows.length > 0 &&
              rows.every((r) => r.datum && r.countryCode) &&
              rows.every((r) => !r.error?.datum && !r.error?.countryCode)
            );
  }, [rows]);

  const handleAddRow = () => setRows((prev) => [...prev, { ...emptyRow }]);
  const handleRemoveRow = (index: number) =>
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  const handleChange = (index: number, field: keyof Row, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      const updated: Row = { ...next[index], [field]: value } as Row;
      // simple validation
      const error: Row["error"] = { ...updated.error };
      if (field === "datum") {
        error.datum = isValidISODate(value) ? undefined : translation("freedays-invalid-date");
      }
      if (field === "countryCode") {
        error.countryCode = value ? undefined : translation("freedays-required");
      }
      next[index] = { ...updated, error };
      return next;
    });
  };

  const handleSave = () => {
    const items: Array<FreeDayCreate> = rows
      .filter((r) => r.datum && r.countryCode)
      .map(({ datum, countryCode, name }) => ({ datum, countryCode, name: name || undefined }));
    onSubmit(items);

    onClose();
  };

  const title = capitalize(
    translation("freedays-bulk-create-title")
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {rows.map((row, idx) => (
            <RowInputs
              key={idx}
              index={idx}
              row={row}
              onChange={handleChange}
              onRemove={handleRemoveRow}
              canRemove={rows.length > 1}
            />
          ))}
          <Box>
            <Button startIcon={<Add />} onClick={handleAddRow} variant="text">
              {capitalize(translation("freedays-add-row"))}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{capitalize(translation("common:cancel"))}</Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSubmit}>
          {capitalize(translation("freedays-save"))}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RowInputs({
  index,
  row,
  onChange,
  onRemove,
  canRemove,
}: {
  index: number;
  row: Row;
  onChange: (index: number, field: keyof Row, value: string) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}) {
  const { t: translation } = useTranslation("freedays");
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
      <TextField
        label={capitalize(translation("freeday-attributes-date"))}
        type="date"
        value={row.datum}
        onChange={(e) => onChange(index, "datum", e.target.value)}
        InputLabelProps={{ shrink: true }}
        fullWidth
        error={!!row.error?.datum}
        helperText={row.error?.datum}
      />
      <TextField
        label={capitalize(translation("freeday-attributes-country-code"))}
        value={row.countryCode}
        onChange={(e) => onChange(index, "countryCode", e.target.value)}
        placeholder={"+49"}
        fullWidth
        error={!!row.error?.countryCode}
        helperText={row.error?.countryCode}
      />
      <TextField
        label={capitalize(translation("freeday-attributes-name"))}
        value={row.name ?? ""}
        onChange={(e) => onChange(index, "name", e.target.value)}
        fullWidth
      />
      <Tooltip title={capitalize(translation("freedays-remove-row"))}>
        <span>
          <IconButton onClick={() => onRemove(index)} disabled={!canRemove} color="error">
            <Delete />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

function isValidISODate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
   return (
          dt.getUTCFullYear() === y &&
          dt.getUTCMonth() === m - 1 &&
          dt.getUTCDate() === d
        );
  }
